import { GameScene } from "./GameScene";
import { setupMinimap } from "./Minimap";
import { profSetEnabled, profReset, profDump } from "./Profiler";

declare const APP_VERSION: string;
declare const APP_DATE: string;
declare const APP_COMMIT_COUNTER: string | number;

// console.log / warn / error に時刻プレフィックスを付与
for (const method of ["log", "warn", "error"] as const) {
    const orig = console[method].bind(console);
    console[method] = (...args: unknown[]) => {
        const now = new Date();
        const ts = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}:${String(now.getSeconds()).padStart(2, "0")}`;
        orig(ts, ...args);
    };
}

console.log(`tommieChat v${APP_VERSION} #${APP_COMMIT_COUNTER} (${APP_DATE})`);

// Android では Web Serial API が使えないのでシリアルテスト関連 UI を非表示にする。
// body.platform-android クラスを起動時に付与し、CSS 側で該当要素を隠す。
if (/Android/.test(navigator.userAgent)) {
    document.body.classList.add("platform-android");
}

// URL パラメータ ?ot=<ゲーム番号> を page load 時に取り込む（仕様書 docs/20 参照）
// 実際の処理は socket 接続確立後に UIPanel 側で行う（遅延呼び出し）
{
    const params = new URLSearchParams(location.search);
    if (params.has("ot")) {
        const ot = params.get("ot") ?? "";
        const w = window as unknown as { __pendingOthelloOpen?: boolean; __pendingOthelloGameNo?: number };
        w.__pendingOthelloOpen = true;
        if (/^\d+$/.test(ot)) {
            w.__pendingOthelloGameNo = parseInt(ot, 10);
            console.log(`URL ?ot=${ot} captured (processed after socket ready)`);
        } else {
            console.log(`URL ?ot captured (no gameNo — open panel only)`);
        }
    }
}

const canvas = document.getElementById("renderCanvas") as HTMLCanvasElement;
if (canvas) {
    const game = new GameScene(canvas);
    setupMinimap(game);

    // ブラウザコンソールからプロファイル操作:
    //   profileStart()  — 計測開始（DevTools Timings に mark/measure 出力）
    //   profileStop()   — 計測停止
    //   profileDump()   — 収集済みデータのサマリをコンソール出力
    const w = window as unknown as Record<string, unknown>;
    w.game = game; // デバッグ用: コンソールから game.nakama.socket.disconnect() 等
    w.profileStart = () => {
        game.profiling = true;
        game['_profileRingIdx'] = 0; game['_profileRingLen'] = 0;
        game.callCounts = {};
        profSetEnabled(true);
        profReset();
        console.log('Profile started');
    };
    w.profileStop = () => {
        game.profiling = false;
        profSetEnabled(false);
        console.log(`Profile stopped — ${game['_profileHistory'].length} frames captured`);
    };
    w.profileDump = () => {
        type PH = { ts: number; playerMove: number; remoteAvatars: number; npc: number; total: number; avatarCount: number };
        const h = game['_profileHistory'] as PH[];
        if (h.length === 0) { console.log('Profile no data — run profileStart() first'); return; }
        const avg = (arr: number[]) => arr.reduce((a, b) => a + b, 0) / arr.length;
        const p95 = (arr: number[]) => { const s = [...arr].sort((a, b) => a - b); return s[Math.floor(s.length * 0.95)]; };
        const max = (arr: number[]) => Math.max(...arr);
        const fields = ['playerMove', 'remoteAvatars', 'npc', 'total'] as const;
        const rows: { name: string; avg: string; p95: string; max: string }[] = fields.map(f => ({
            name: f,
            avg: avg(h.map(r => r[f])).toFixed(3) + 'ms',
            p95: p95(h.map(r => r[f])).toFixed(3) + 'ms',
            max: max(h.map(r => r[f])).toFixed(3) + 'ms',
        }));
        rows.push({ name: 'avatarCount', avg: avg(h.map(r => r.avatarCount)).toFixed(0), p95: '-', max: max(h.map(r => r.avatarCount)).toFixed(0) });
        console.log(`Profile ${h.length} frames`);
        console.table(rows);
        const md = game.nakama.matchDataProfile;
        console.log(`Profile onmatchdata: ${md.calls} calls/s, total=${md.totalMs.toFixed(1)}ms/s, max=${md.maxMs.toFixed(2)}ms`);
        const ul = game.userListProfile;
        console.log(`Profile userList: ${ul.calls} renders/s, total=${ul.totalMs.toFixed(1)}ms/s, max=${ul.maxMs.toFixed(2)}ms, users=${ul.userCount}`);
        // 関数呼び出し回数（イベント系）
        const cc = game.callCounts;
        const allFuncs = ['onPresenceJoin', 'onPresenceNewJoin', 'onPresenceLeave', 'onMatchPresenceJoin', 'onMatchPresenceLeave', 'removeRemoteAvatar', 'scheduleRenderUserList', 'renderUserList'];
        const elapsed = h.length > 1 ? (h[h.length - 1].ts - h[0].ts) / 1000 : 0;
        const ccRows = allFuncs.map(k => ({ function: k, calls: cc[k] ?? 0, 'calls/s': elapsed > 0 ? Math.round(((cc[k] ?? 0) / elapsed) * 10) / 10 : 0 }));
        console.log('Profile function call counts:');
        console.table(ccRows);
        // 全関数プロファイル（時間計測）
        const fp = profDump();
        if (fp.length > 0) {
            const fpRows = fp.map(f => ({
                name: f.name,
                calls: f.calls,
                totalMs: Math.round(f.totalMs * 100) / 100,
                avgUs: Math.round(f.avgUs * 10) / 10,
                maxUs: Math.round(f.maxUs),
            }));
            console.log('Profile browser function profile (totalMs desc):');
            console.table(fpRows);
        }
    };
} else {
    console.error("Canvas element 'renderCanvas' not found!");
}
